import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@workspace/ui/components/dialog'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { Popover, PopoverAnchor, PopoverContent } from '@workspace/ui/components/popover'
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@workspace/ui/components/sheet'
import { Textarea } from '@workspace/ui/components/textarea'
import { cn } from '@workspace/ui/lib/utils'
import { Eye, Sparkles } from 'lucide-react'
import { type ComponentType, type ReactNode, useState } from 'react'
import { useMediaQuery } from '../../../../hooks/use-media-query'
import { copy } from '../../copy/investor-copy'
import { useSubmitOnce } from '../../hooks/use-submit-once'

type Choice = 'material' | 'condition'

const OPTION = 'flex w-full cursor-pointer items-start gap-3 rounded-lg border p-3 text-left'

function Option({
  checked,
  title,
  hint,
  onSelect,
  children,
}: {
  checked: boolean
  title: string
  hint?: string
  onSelect: () => void
  /** What belongs inside the option once it is selected (the condition's words). */
  children?: ReactNode
}) {
  return (
    <label
      className={cn(
        OPTION,
        'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary/50',
        checked
          ? 'border-primary/40 bg-primary/5'
          : 'border-border transition-colors hover:bg-white/[0.04]'
      )}
    >
      <input
        type="radio"
        name="follow-choice"
        checked={checked}
        onChange={onSelect}
        className="sr-only"
      />
      <span
        aria-hidden
        className={cn(
          'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border',
          checked ? 'border-primary' : 'border-border'
        )}
      >
        {checked && <span className="size-2 rounded-full bg-primary" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-foreground">{title}</span>
        {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
        {children}
      </span>
    </label>
  )
}

interface WindowProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** "follow" starts a follow (consent and allowance shown); "edit" changes an existing one. */
  mode: 'follow' | 'edit'
  startupName: string
  /** The words the follow holds now (edit), or null for material updates. */
  initialCondition?: string | null
  /** The update the investor's AI would wait for, to start from. */
  suggested?: string | null
  /** "3 of 5 free follows used"; shown when following. */
  allowanceLine?: string
  pending: boolean
  onSubmit: (condition: string | null) => void
  /** The element the window opens from on a wide screen; without one it is a centred dialog. */
  anchor?: Element | null
}

type TitleParts = {
  Title: ComponentType<{ children: ReactNode }>
  Description: ComponentType<{ children: ReactNode }>
}

/** The window's content: the same in the popover, the bottom sheet and the dialog. */
function FollowForm({
  mode,
  startupName,
  initialCondition,
  suggested,
  allowanceLine,
  pending,
  onSubmit,
  onOpenChange,
  Title,
  Description,
}: Omit<WindowProps, 'open' | 'anchor'> & TitleParts) {
  const editing = mode === 'edit'
  const [choice, setChoice] = useState<Choice>(
    initialCondition || (!editing && suggested) ? 'condition' : 'material'
  )
  const [text, setText] = useState(initialCondition ?? suggested ?? '')
  const trimmed = text.trim()
  const disabled = pending || (choice === 'condition' && trimmed === '')
  const once = useSubmitOnce(pending)
  const submit = () => !disabled && once(() => onSubmit(choice === 'condition' ? trimmed : null))

  return (
    <>
      <Title>
        {editing ? copy('follow.edit.title') : copy('follow.title', { startup: startupName })}
      </Title>
      <Description>{editing ? startupName : copy('follow.sub')}</Description>

      <fieldset className="mt-3 space-y-2">
        <Option
          checked={choice === 'material'}
          title={copy('follow.material')}
          hint={copy('follow.material.hint')}
          onSelect={() => setChoice('material')}
        />
        <Option
          checked={choice === 'condition'}
          title={copy('follow.condition')}
          hint={choice === 'condition' ? undefined : copy('follow.condition.hint')}
          onSelect={() => setChoice('condition')}
        >
          {choice === 'condition' && (
            <span className="mt-2.5 block">
              <span className="flex items-center justify-between gap-2 text-xs font-medium text-foreground/80">
                <span>{copy('follow.tellMe')}</span>
                {suggested && text === suggested && (
                  <span className="inline-flex items-center gap-1 font-normal text-muted-foreground">
                    <Sparkles className="size-3" />
                    {copy('follow.suggested')}
                  </span>
                )}
              </span>
              <Textarea
                aria-label={copy('follow.tellMe')}
                rows={3}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={copy('follow.placeholder')}
                className="mt-1.5 block w-full resize-none rounded-md border border-primary bg-[#0F0F10] px-3 py-2 text-sm leading-relaxed shadow-[0_0_0_3px_rgba(249,115,22,0.12)]"
              />
            </span>
          )}
        </Option>
      </fieldset>

      {!editing && (
        <p className="mt-3 flex items-start gap-2 text-xs text-muted-foreground">
          <Eye className="mt-0.5 size-3.5 shrink-0" />
          {copy('follow.consent')}
        </p>
      )}

      {editing ? (
        <div className="mt-3 flex items-center justify-end gap-4">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            {copy('follow.edit.cancel')}
          </button>
          <CtaButton size="sm" disabled={disabled} loading={pending} onClick={submit}>
            {copy('follow.edit.save')}
          </CtaButton>
        </div>
      ) : (
        <>
          <CtaButton
            size="md"
            fullWidth
            className="mt-3"
            disabled={disabled}
            loading={pending}
            onClick={submit}
          >
            {copy('follow.submit')}
          </CtaButton>
          {allowanceLine && (
            <p className="mt-2 text-center text-xs text-muted-foreground">{allowanceLine}</p>
          )}
        </>
      )}
    </>
  )
}

const PlainTitle = ({ children }: { children: ReactNode }) => (
  <h2 className="text-sm font-semibold text-foreground">{children}</h2>
)
const PlainDescription = ({ children }: { children: ReactNode }) => (
  <p className="mt-1 text-xs text-muted-foreground">{children}</p>
)
const DialogHeading = ({ children }: { children: ReactNode }) => (
  <DialogTitle className="text-lg font-semibold">{children}</DialogTitle>
)
const DialogSub = ({ children }: { children: ReactNode }) => (
  <DialogDescription className="mt-1 text-sm text-muted-foreground">{children}</DialogDescription>
)
const SheetHeading = ({ children }: { children: ReactNode }) => (
  <SheetTitle className="text-lg font-semibold text-foreground">{children}</SheetTitle>
)
const SheetSub = ({ children }: { children: ReactNode }) => (
  <SheetDescription className="mt-1 text-sm text-muted-foreground">{children}</SheetDescription>
)

/**
 * What to follow a startup for: every material update, or a condition in the investor's own words.
 * A popover under the button from 1024px, a bottom sheet below that, and a centred dialog where there
 * is no button to open it from.
 */
export function FollowWindow({ open, onOpenChange, anchor, ...form }: WindowProps) {
  const wide = useMediaQuery('(min-width: 1024px)')

  if (!wide) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          className="max-h-[85vh] gap-0 overflow-y-auto rounded-t-2xl bg-card p-5"
        >
          <FollowForm
            {...form}
            onOpenChange={onOpenChange}
            Title={SheetHeading}
            Description={SheetSub}
          />
        </SheetContent>
      </Sheet>
    )
  }
  if (anchor) {
    return (
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverAnchor virtualRef={{ current: anchor }} />
        <PopoverContent>
          <FollowForm
            {...form}
            onOpenChange={onOpenChange}
            Title={PlainTitle}
            Description={PlainDescription}
          />
        </PopoverContent>
      </Popover>
    )
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        overlayClassName="bg-black/60"
        className="max-w-md gap-0 rounded-2xl bg-card p-6 sm:max-w-md"
      >
        <FollowForm
          {...form}
          onOpenChange={onOpenChange}
          Title={DialogHeading}
          Description={DialogSub}
        />
      </DialogContent>
    </Dialog>
  )
}
